"use client";

import { createT } from "@/app/[lang]/_utils/create-t";
import type { Lang } from "@/langs";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@borderless/ui/components/chart";
import { cn } from "@borderless/ui/lib/utils";
import * as React from "react";
import { Cell, Label, Pie, PieChart } from "@borderless/ui/lib/recharts";
import dictionary from "../t.json";

const t = createT(dictionary);

interface DonutWithTextChartProps {
  lang: Lang;
  data: {
    label: string;
    num: number;
    fill: string;
  }[];
  className?: string;
  centerTextClassName?: string;
  labelClassName?: string;
}

export function DonutWithTextChart(props: DonutWithTextChartProps) {
  const chartData = props.data.filter((d) => d.num > 0);

  const total = React.useMemo(() => {
    return props.data.reduce((acc, curr) => acc + curr.num, 0);
  }, [props.data]);

  // Create dynamic chart config based on input data
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {
      num: {
        label: t("Students", props.lang, true),
      },
    };

    props.data.forEach((item, index) => {
      config[item.label] = {
        label: item.label,
        color: item.fill ?? `hsl(var(--chart-${index + 1}))`,
      };
    });

    return config;
  }, [props.data]);

  return (
    <ChartContainer
      config={chartConfig}
      className={cn("mx-auto aspect-square max-h-[200px]", props.className)}
    >
      <PieChart className="[&_svg.recharts-surface]:overflow-visible">
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent hideLabel />}
        />
        <Pie
          data={chartData}
          dataKey="num"
          nameKey="label"
          innerRadius={60}
          strokeWidth={5}
          startAngle={90}
          endAngle={90 - 360}
          labelLine={false}
          label={(entry: {
            cx: number;
            cy: number;
            midAngle: number;
            fill: string;
            label: string;
            value: number;
          }) => {
            const RADIAN = Math.PI / 180;
            const radius = 80;
            const x = entry.cx + radius * Math.cos(-entry.midAngle * RADIAN);
            const y = entry.cy + radius * Math.sin(-entry.midAngle * RADIAN);

            return (
              <text
                x={x}
                y={y}
                fill={entry.fill}
                textAnchor={x > entry.cx ? "start" : "end"}
                dominantBaseline="central"
                className="text-xs font-medium"
              >
                <tspan x={x} dy="0">
                  {entry.label}
                </tspan>
                <tspan x={x} dy="1.2em">
                  {((entry.value / total) * 100).toFixed(1)}%
                </tspan>
              </text>
            );
          }}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className={cn(
                        "fill-foreground text-3xl font-bold",
                        props.centerTextClassName,
                      )}
                    >
                      {total.toLocaleString()}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy ?? 0) + 24}
                      className={cn(
                        "fill-muted-foreground",
                        props.labelClassName,
                      )}
                    >
                      Students
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
}
